import {
  Button,
  Link,
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@portfolio/components'
import { NavigationNestedItem } from '@portfolio/models'
import { fonts, styled } from '@portfolio/styles'
import { useTranslation } from 'react-i18next'
import { ChevronBig } from '@portfolio/icons'

type MobileNavigationOverlayProps = {
  onBack: () => void
  isVisible: boolean
  selectedNavItem: NavigationNestedItem | undefined | null
}

const NestedLinkContainer = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '$16',
  color: '$white',

  '&:last-child': {
    marginBottom: '$48',
  },
})

const NestedLink = styled(Link, {
  ...fonts.ui05,
  display: 'flex',
  width: 'fit-content',
  transition: 'background-color 0.2s ease',
  borderBottom: '1px solid $white',
})

const NavigationPanel = styled('div', {
  position: 'absolute',
  top: '$headerHeight',
  left: 0,
  right: 0,
  background: '$black',
  zIndex: 1,
  display: 'flex',
  flexDirection: 'column',
  visibility: 'hidden',
  overflowX: 'hidden',
  overflowY: 'auto',
  maxHeight: 'calc(100% - 2 * $headerHeight)',
  overscrollBehavior: 'none',

  variants: {
    isVisible: {
      true: {
        visibility: 'visible',
      },
    },
  },
})

const NavigationHeader = styled('div', {
  position: 'fixed',
  top: '$headerHeight',
  left: 0,
  background: '$black',
  width: '100%',
  height: '$64',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0 $20',
  zIndex: 1,
})

const BackButton = styled(Button, {
  display: 'flex',
  alignItems: 'center',
  gap: '$8',
  projectFont: 'body03',
  opacity: 0,
  transition: 'all 0.4s ease',

  variants: {
    isVisible: {
      true: {
        opacity: 1,
      },
    },
  },
})

const NavigationContent = styled('div', {
  padding: '0 $20',
  marginTop: '$144',
  transform: 'translateX(100%)',
  transition: 'all 0.2s ease',

  variants: {
    isVisible: {
      true: {
        transform: 'translateX(0)',
      },
    },
  },
})

const SectionTitle = styled('h2', {
  position: 'sticky',
  top: '$64',
  background: '$black',
  projectFont: 'heading05',
  padding: '$24 0',
  borderBottom: '1px solid $white',
  zIndex: 1,
})

const ViewAllLink = styled(Link, {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '$8',
  padding: '$24 0',
})

const LinkContainer = styled('div', {
  '& a': {
    projectFont: 'heading07',
  },
})

const BackButtonIcon = styled('span', {
  display: 'flex',
  cursor: 'pointer',
  alignSelf: 'center',
  transform: 'rotate(180deg)',
})

export const MobileNestedNavigationItem = ({
  onBack,
  isVisible,
  selectedNavItem,
}: MobileNavigationOverlayProps) => {
  const { t } = useTranslation('header')
  const nestedItemGroups = selectedNavItem?.linkGroups

  return (
    <NavigationPanel isVisible={isVisible}>
      <NavigationHeader>
        <BackButton appearance="text" onClick={onBack} isVisible={isVisible}>
          <BackButtonIcon>
            <ChevronBig />
          </BackButtonIcon>
          {t('back')}
        </BackButton>
      </NavigationHeader>
      <NavigationContent isVisible={isVisible}>
        {selectedNavItem && (
          <>
            <SectionTitle>{selectedNavItem.ctaLabel}</SectionTitle>
            <LinkContainer>
              {selectedNavItem.ctaLink && (
                <ViewAllLink appearance="grid" {...selectedNavItem.ctaLink}>
                  {t('groupOverviewLabel', { label: selectedNavItem.ctaLabel })}
                  <ChevronBig />
                </ViewAllLink>
              )}
            </LinkContainer>
          </>
        )}
        {!!nestedItemGroups?.length && (
          <Accordion>
            {nestedItemGroups.map((group) => (
              <AccordionItem
                key={group._key}
                value={group._key}
                isDivider={false}
              >
                <AccordionTrigger icon="chevron">
                  {group.groupLabel}
                </AccordionTrigger>
                <AccordionContent>
                  <NestedLinkContainer>
                    {group.links?.map((link) =>
                      link.link ? (
                        <NestedLink key={link._key} {...link.link}>
                          {link.title}
                        </NestedLink>
                      ) : null,
                    )}
                  </NestedLinkContainer>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        )}
      </NavigationContent>
    </NavigationPanel>
  )
}
